import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { User, BookOpen, Clock, Check, AlertTriangle, Trash2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const DAYS_LABELS = {
  'monday': 'Segunda-feira',
  'tuesday': 'Terça-feira', 
  'wednesday': 'Quarta-feira',
  'thursday': 'Quinta-feira',
  'friday': 'Sexta-feira'
};

export default function ScheduleEditor({ 
  open, 
  onOpenChange, 
  day, 
  timeSlot,
  timeSlotLabel,
  turmaName,
  linkedTeachers = [],
  existingSchedule = null,
  onSave,
  onDelete,
  checkConflict
}) {
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reset state quando abrir
  useEffect(() => {
    if (open) {
      if (existingSchedule) {
        // Preencher com dados existentes
        const teacher = linkedTeachers.find(t => t.id === existingSchedule.employee_id);
        setSelectedTeacher(teacher || null);
        setSelectedSubject({
          name: existingSchedule.subject_name,
          assignment_id: existingSchedule.assignment_id
        });
      } else {
        setSelectedTeacher(null);
        setSelectedSubject(null);
      }
      setConflictWarning(null);
    }
  }, [open, existingSchedule, linkedTeachers]);

  // Verificar conflito quando selecionar professor
  useEffect(() => {
    const verifyConflict = async () => {
      if (selectedTeacher && checkConflict) {
        const conflict = await checkConflict(selectedTeacher.id, day, timeSlot, existingSchedule?.id);
        setConflictWarning(conflict);
      } else {
        setConflictWarning(null);
      }
    };
    verifyConflict();
  }, [selectedTeacher, day, timeSlot, checkConflict, existingSchedule]);

  const handleSelectTeacher = (teacher) => {
    if (selectedTeacher?.id === teacher.id) {
      // Desselecionar
      setSelectedTeacher(null);
      setSelectedSubject(null);
    } else {
      setSelectedTeacher(teacher);
      setSelectedSubject(null); // Reset disciplina ao mudar professor
    }
  };

  const handleSelectSubject = (subject) => {
    if (selectedSubject?.name === subject.name) {
      setSelectedSubject(null);
    } else {
      setSelectedSubject({
        name: subject.name,
        assignment_id: subject.assignment_id
      });
    }
  };

  const handleSave = async () => {
    if (!selectedTeacher || !selectedSubject) {
      toast.error('Selecione professor e disciplina');
      return;
    }

    if (conflictWarning) {
      toast.error(`Conflito: ${selectedTeacher.name} já tem aula às ${timeSlotLabel} em ${conflictWarning.turma}`);
      return;
    }

    setSaving(true);
    try {
      await onSave({
        day,
        time_slot: timeSlot,
        employee_id: selectedTeacher.id,
        employee_name: selectedTeacher.name,
        subject_name: selectedSubject.name,
        assignment_id: selectedSubject.assignment_id
      });
      onOpenChange(false);
      toast.success(existingSchedule ? 'Horário atualizado!' : 'Horário adicionado!');
    } catch (error) {
      toast.error('Erro ao salvar: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingSchedule) return;
    
    if (!window.confirm('Deseja excluir este horário?')) return;
    
    try {
      await onDelete(existingSchedule.id);
      onOpenChange(false);
      toast.success('Horário excluído!');
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const teacherSubjects = selectedTeacher?.subjects || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header com gradiente */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base font-bold">
            <Clock className="w-4 h-4" />
            {existingSchedule ? 'Editar Aula' : 'Adicionar Aula'}
          </DialogTitle>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-indigo-100">
            <Badge variant="secondary" className="bg-white/20 text-white text-xs py-0">
              {DAYS_LABELS[day] || day}
            </Badge>
            <Badge variant="secondary" className="bg-white/20 text-white text-xs py-0">
              {timeSlotLabel}
            </Badge>
          </div>
          <p className="text-xs text-indigo-200 mt-1">{turmaName}</p>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Alerta de Conflito */}
          {conflictWarning && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Conflito!</strong> {selectedTeacher?.name} já tem aula neste horário em <strong>{conflictWarning.turma}</strong>.
              </AlertDescription>
            </Alert>
          )}

          {/* Professores Vinculados */}
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-2">
              <User className="w-3 h-3" />
              Selecione o Professor
            </Label>
            
            {linkedTeachers.length === 0 ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <p className="font-medium text-xs">Nenhum professor vinculado a esta turma</p>
                <p className="text-xs mt-1">Vincule professores na área "Vinculação de Professores"</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {linkedTeachers.map(teacher => {
                  const isSelected = selectedTeacher?.id === teacher.id;
                  return (
                    <button
                      key={teacher.id}
                      type="button"
                      onClick={() => handleSelectTeacher(teacher)}
                      className={cn(
                        "p-2 rounded-lg border-2 text-left transition-all",
                        isSelected 
                          ? "bg-indigo-600 border-indigo-600 text-white shadow-lg scale-[1.02]" 
                          : "bg-white border-slate-200 hover:border-indigo-400 hover:shadow"
                      )}
                    >
                      <p className={cn("font-semibold text-xs", !isSelected && "text-slate-800")}>
                        {teacher.name}
                      </p>
                      <p className={cn("text-xs mt-0.5", isSelected ? "text-indigo-100" : "text-slate-500")}>
                        {teacher.subjects?.length || 0} disciplina(s)
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Disciplinas do Professor */}
          {selectedTeacher && (
            <div className="border-t pt-3">
              <Label className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                <BookOpen className="w-3 h-3" />
                Disciplina de {selectedTeacher.name}
              </Label>
              
              {teacherSubjects.length === 0 ? (
                <p className="text-xs text-slate-500">Nenhuma disciplina cadastrada</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {teacherSubjects.map(subject => {
                    const isSelected = selectedSubject?.name === subject.name;
                    return (
                      <button
                        key={subject.id || subject.name}
                        type="button"
                        onClick={() => handleSelectSubject(subject)}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                          isSelected
                            ? "bg-emerald-500 text-white shadow-md"
                            : "bg-white border border-slate-300 text-slate-700 hover:border-emerald-400 hover:bg-emerald-50"
                        )}
                      >
                        {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                        {subject.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Preview da Seleção */}
          {selectedTeacher && selectedSubject && (
            <div className={cn(
              "border rounded-lg p-3 flex items-center gap-2",
              conflictWarning 
                ? "bg-red-50 border-red-200" 
                : "bg-emerald-50 border-emerald-200"
            )}>
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                conflictWarning ? "bg-red-500" : "bg-emerald-500"
              )}>
                {conflictWarning ? (
                  <X className="w-4 h-4 text-white" />
                ) : (
                  <Check className="w-4 h-4 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "font-semibold text-sm truncate",
                  conflictWarning ? "text-red-800" : "text-emerald-800"
                )}>
                  {selectedSubject.name}
                </p>
                <p className={cn(
                  "text-xs truncate",
                  conflictWarning ? "text-red-600" : "text-emerald-600"
                )}>
                  {selectedTeacher.name} • {DAYS_LABELS[day]} {timeSlotLabel}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer com botões - SEMPRE VISÍVEL */}
        <div className="flex items-center gap-2 p-3 bg-slate-50 border-t shrink-0">
          {existingSchedule && (
            <Button 
              type="button" 
              variant="destructive" 
              size="sm" 
              onClick={handleDelete}
              className="text-xs"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Excluir
            </Button>
          )}
          <div className="flex-1" />
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => onOpenChange(false)}
            className="text-xs"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            size="sm"
            disabled={!selectedTeacher || !selectedSubject || saving || conflictWarning}
            className="bg-indigo-600 hover:bg-indigo-700 px-4 text-xs"
          >
            {saving ? 'Salvando...' : (existingSchedule ? 'Salvar' : 'Adicionar')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
